import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import {
  TREQUEST_DELETE_TEAM,
  TREQUEST_PLANI8_EMPLOYEE_LIST,
  TREQUEST_UPDATE_PLANI8_EMPLOYEE_LIST,
  TREQUEST_UPDATE_TEAM,
  TREQUEST_ARCHIVE_USER,
  TREQUEST_GET_PENDING_TNA_STEPS,
  TRESPONSE_GET_PENDING_TNA_STEPS,
} from '@monorepo/types';
import { TUserPaginationOption } from '@monorepo/ui';
import { select, Store } from '@ngrx/store';
import { lastValueFrom, of } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { TPlani8State } from '../reducers';
import * as employeeListActions from './employee-list.actions';
import * as employeeListSelectors from './employee-list.selectors';

export const EMPLOYEE_LISTING_ITEMS_PER_PAGE_LIST = [10, 50, 100, 200];
@Injectable({
  providedIn: 'root',
})
export class EmployeeListFacade {
  constructor(
    private store: Store<TPlani8State>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private plani8DataService: Plani8DataService,
  ) {}

  fetchEmployeeListInfo(payload: TREQUEST_PLANI8_EMPLOYEE_LIST) {
    this.store.dispatch(new employeeListActions.GetEmployeeList(payload));
  }

  updateEmployeeList(updatePayload: TREQUEST_UPDATE_PLANI8_EMPLOYEE_LIST, fetchPayload: TREQUEST_PLANI8_EMPLOYEE_LIST) {
    return this.store.dispatch(new employeeListActions.UpdateEmployees(updatePayload, fetchPayload));
  }

  getEmployeeListInfo() {
    return this.store.pipe(select(employeeListSelectors.getEmployeeListInfo));
  }

  fetchTeamListInfo() {
    this.store.dispatch(new employeeListActions.GetTeamList());
  }

  deleteTeam(payload: TREQUEST_DELETE_TEAM, fetchPayload: TREQUEST_PLANI8_EMPLOYEE_LIST) {
    this.store.dispatch(new employeeListActions.DeleteTeams(payload, fetchPayload));
  }

  updateTeam(payload: TREQUEST_UPDATE_TEAM, fetchPayload: TREQUEST_PLANI8_EMPLOYEE_LIST) {
    this.store.dispatch(new employeeListActions.UpdateTeams(payload, fetchPayload));
  }

  getTeamListInfo() {
    return this.store.pipe(select(employeeListSelectors.getTeamListInfo));
  }

  getUserPaginationOption() {
    return this.store.pipe(select(employeeListSelectors.getUserPaginationOption));
  }

  setUserPaginationOption(option: TUserPaginationOption) {
    return this.store.dispatch(new employeeListActions.SetUserPaginationOption(option));
  }

  async fetchPendingTNASteps(payload: TREQUEST_GET_PENDING_TNA_STEPS): Promise<TRESPONSE_GET_PENDING_TNA_STEPS | null> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.getPendingTNASteps(payload).pipe(
        catchError(() => of(null)),
        finalize(dismiss),
      ),
    );
  }

  async archiveUser(payload: TREQUEST_ARCHIVE_USER): Promise<boolean> {
    const loaderEl = await this.appNotificationUtilClass.presentLoading();
    const dismiss = () => this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
    return lastValueFrom(
      this.plani8DataService.archiveUser(payload).pipe(
        map(() => true),
        catchError(() => of(false)),
        finalize(dismiss),
      ),
    );
  }
}

export type TGetEmployeeListInfo = ReturnType<EmployeeListFacade['getEmployeeListInfo']>;
export type TGetTeamListInfo = ReturnType<EmployeeListFacade['getTeamListInfo']>;
